import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
} from "@angular/core";
import { View } from "../../store/students/state";
import { cloneDeep, isEqual, map, filter, find } from "lodash";

@Component({
  selector: "list-settings-details-panel",
  templateUrl: "./list-settings-details-panel.component.html",
  styleUrls: ["./list-settings-details-panel.component.scss"],
})
export class ListSettingsDetailsPanelComponent implements OnInit, OnChanges {
  @Input() selectedItem;
  @Input() columns;
  @Input() schools;
  @Input() isAdminUser;
  @Input() isSLUser;
  @Input() isStarterUser;
  @Input() error;
  @Input() isSaveSuccessful;
  @Input() views;
  @Input() groups;
  @Input() hasChangesToSave: boolean;
  @Input() selectedItemType: string;

  @Output() deleteViewEvent = new EventEmitter();
  @Output() saveViewEvent = new EventEmitter();
  @Output() changesToSaveEvent = new EventEmitter();
  @Output() updatedItemEvent = new EventEmitter();

  updatedItem;
  sharedWithAdmins: boolean;
  sharedWithSchoolLeaders: boolean;
  sharedWithTeachers: boolean;
  selectedSchoolLeaderSchools: Array<string> = [];
  selectedTeacherSchools: Array<string> = [];
  hasDuplicateViewName: boolean;
  hasDuplicateGroupName: boolean;

  constructor() {}

  ngOnInit() {}

  ngOnChanges(change) {
    if (change && change.selectedItem) {
      this.updatedItem = cloneDeep(this.selectedItem);
      if (this.selectedItem) {
        this.sharedWithAdmins = this.selectedItem.sharedWithAdmins;
        if (
          this.selectedItem.sharedWithSchoolLeadersAtSchoolIds &&
          this.selectedItem.sharedWithSchoolLeadersAtSchoolIds.length > 0
        ) {
          this.sharedWithSchoolLeaders = true;
          this.selectedSchoolLeaderSchools = filter(this.schools, (school) => {
            return (
              this.selectedItem.sharedWithSchoolLeadersAtSchoolIds.indexOf(
                school["systemSchoolId"]
              ) > -1
            );
          });
        } else {
          this.sharedWithSchoolLeaders = false;
          this.selectedSchoolLeaderSchools = [];
        }
        if (
          this.selectedItem.sharedWithTeachersAtSchoolIds &&
          this.selectedItem.sharedWithTeachersAtSchoolIds.length > 0
        ) {
          this.sharedWithTeachers = true;
          this.selectedTeacherSchools = filter(this.schools, (school) => {
            return (
              this.selectedItem.sharedWithTeachersAtSchoolIds.indexOf(
                school["systemSchoolId"]
              ) > -1
            );
          });
        } else {
          this.sharedWithTeachers = false;
          this.selectedTeacherSchools = [];
        }
      }
    }
  }

  nameUpdated(event) {
    this.updatedItem.name = event;
    if (this.selectedItemType == "group") {
      this.hasDuplicateGroupName =
        find(this.groups, ["name", this.updatedItem.name]) &&
        this.selectedItem.name != this.updatedItem.name;
      this.hasDuplicateViewName = null;
    } else {
      this.hasDuplicateViewName =
        find(this.views, ["name", this.updatedItem.name]) &&
        this.selectedItem.name != this.updatedItem.name;
      this.hasDuplicateGroupName = null;
    }
    this.clearSaveMsgAndCheckForNewChanges();
  }

  descriptionUpdated(event) {
    this.updatedItem.description = event;
    this.clearSaveMsgAndCheckForNewChanges();
  }

  sharedWithSchoolLeaderSchoolsUpdated(event) {
    this.updatedItem.sharedWithSchoolLeadersAtSchoolIds = map(
      event,
      "systemSchoolId"
    );
    this.clearSaveMsgAndCheckForNewChanges();
  }

  sharedWithTeacherSchoolsUpdated(event) {
    this.updatedItem.sharedWithTeachersAtSchoolIds = map(
      event,
      "systemSchoolId"
    );
    this.clearSaveMsgAndCheckForNewChanges();
  }

  sharedWithAdminsUpdated(event) {
    this.updatedItem.sharedWithAdmins = event && event.checked;
    this.clearSaveMsgAndCheckForNewChanges();
  }

  toggleShareWithSchoolLeaders() {
    if (!this.sharedWithSchoolLeaders) {
      // we've un-toggled "share with school leaders", so we want to clear the sharedWithSchoolLeadersAtSchoolIds array and selectedSchoolLeaderSchools array
      this.updatedItem.sharedWithSchoolLeadersAtSchoolIds = [];
      this.selectedSchoolLeaderSchools = [];
    }
    this.clearSaveMsgAndCheckForNewChanges();
  }

  toggleShareWithTeachers() {
    if (!this.sharedWithTeachers) {
      // we've un-toggled "share with teachers", so we want to clear the sharedWithTeachersAtSchoolIds array and selectedTeacherSchools array
      this.updatedItem.sharedWithTeachersAtSchoolIds = [];
      this.selectedTeacherSchools = [];
    }
    this.clearSaveMsgAndCheckForNewChanges();
  }

  addGroupCriteria(event) {
    this.updatedItem = {
      ...this.updatedItem,
      efficacyCriteria: [...this.updatedItem.efficacyCriteria, event],
    };
    this.clearSaveMsgAndCheckForNewChanges();
  }

  deleteGroupCriteria(event) {
    this.updatedItem = {
      ...this.updatedItem,
      efficacyCriteria: filter(
        this.updatedItem.efficacyCriteria,
        (criteria) => {
          return criteria.student_kpi_id != event;
        }
      ),
    };
    this.clearSaveMsgAndCheckForNewChanges();
  }

  checkSaveDisabled() {
    return (
      (this.sharedWithSchoolLeaders &&
        (!this.updatedItem.sharedWithSchoolLeadersAtSchoolIds ||
          (this.updatedItem.sharedWithSchoolLeadersAtSchoolIds &&
            this.updatedItem.sharedWithSchoolLeadersAtSchoolIds.length ==
              0))) ||
      (this.sharedWithTeachers &&
        (!this.updatedItem.sharedWithTeachersAtSchoolIds ||
          (this.updatedItem.sharedWithTeachersAtSchoolIds &&
            this.updatedItem.sharedWithTeachersAtSchoolIds.length == 0))) ||
      this.updatedItem.name == "" ||
      this.hasDuplicateViewName ||
      this.hasDuplicateGroupName
    );
  }

  clearSaveMsgAndCheckForNewChanges() {
    this.isSaveSuccessful = null;
    let hasChangesToSave =
      this.updatedItem.name != this.selectedItem.name ||
      this.updatedItem.description != this.selectedItem.description ||
      this.updatedItem.sharedWithAdmins != this.selectedItem.sharedWithAdmins ||
      !isEqual(
        [...this.updatedItem.sharedWithSchoolLeadersAtSchoolIds].sort(),
        [...this.selectedItem.sharedWithSchoolLeadersAtSchoolIds].sort()
      ) ||
      !isEqual(
        [...this.updatedItem.sharedWithTeachersAtSchoolIds].sort(),
        [...this.selectedItem.sharedWithTeachersAtSchoolIds].sort()
      );
    if (this.selectedItemType == "group") {
      // also check for efficacy criteria changes for adding/editing groups
      hasChangesToSave =
        hasChangesToSave ||
        !isEqual(
          [...this.updatedItem.efficacyCriteria].sort(),
          [...this.selectedItem.efficacyCriteria].sort()
        );
    }
    this.changesToSaveEvent.emit(hasChangesToSave);
    this.updatedItemEvent.emit(this.updatedItem);
  }
}
